/**
 * University Registry — pure dispatcher.
 *
 * Every per-section rule (weight formula, track minimums, comparison
 * threshold, displayed min-rate, label key, gender/track filters) lives
 * inside that section's own data module. This file only routes calls.
 *
 * A change to one section's percentages, calculations, or specialty filters
 * is structurally incapable of affecting any other section because no
 * branching logic exists here.
 */

import type { College, CollegeWeights, Department, Gender, Scores, StepAlert, StepAlertLine, Track } from "./sharedTypes";

import * as kuwaitUni from "./kuwaitUniversityData";
import * as aasu from "./abdullahAlSalemData";
import * as foreignMissions from "./foreignMissionsData";
import * as internalMissions from "./internalMissionsData";
import * as paaet from "./paaetData";

export type UniversityId =
    | "kuwait_university"
    | "abdullah_al_salem"
    | "foreign_missions"
    | "internal_missions"
    | "paaet";

export interface UniversityInfo {
    id: UniversityId;
    nameKey: string;
    emoji: string;
    order: number;
}

export const allUniversities: UniversityInfo[] = [
    { id: "kuwait_university", nameKey: "dream_university_uni_kuwait", emoji: "🏛️", order: 1 },
    { id: "abdullah_al_salem", nameKey: "dream_university_uni_aasu", emoji: "🎓", order: 2 },
    { id: "foreign_missions", nameKey: "dream_university_uni_foreign_missions", emoji: "🌍", order: 3 },
    { id: "internal_missions", nameKey: "dream_university_uni_internal_missions", emoji: "🏠", order: 4 },
    { id: "paaet", nameKey: "dream_university_uni_paaet", emoji: "⚙️", order: 5 },
];

const visibleIds: UniversityId[] = ["kuwait_university", "abdullah_al_salem", "foreign_missions", "internal_missions", "paaet"];
export const universities: UniversityInfo[] = allUniversities.filter((u) => visibleIds.includes(u.id));

function getUniversityModule(id: UniversityId) {
    switch (id) {
        case "kuwait_university":
            return kuwaitUni;
        case "abdullah_al_salem":
            return aasu;
        case "foreign_missions":
            return foreignMissions;
        case "internal_missions":
            return internalMissions;
        case "paaet":
            return paaet;
    }
}

// ── Pure dispatch wrappers ──────────────────────────────────────────────

export function getColleges(universityId: UniversityId, gender?: Gender, track?: Track | null): College[] {
    return getUniversityModule(universityId).getColleges(gender, track);
}

export function getDepartments(universityId: UniversityId, gender?: Gender, track?: Track | null): Department[] {
    return getUniversityModule(universityId).getDepartmentsForFilters(gender, track);
}

export function getCollegeMinRate(universityId: UniversityId, collegeKey: string, gender?: Gender, track?: Track | null): number {
    return getUniversityModule(universityId).getCollegeMinRate(collegeKey, gender, track);
}

export function getDisplayedMinRate(
    universityId: UniversityId,
    department: Department | null,
    track: Track | null | undefined,
    weights: CollegeWeights,
    fallbackMinRate: number,
): number {
    return getUniversityModule(universityId).getDisplayedMinRate(department, track, weights, fallbackMinRate);
}

export function getDisplayedMinRateLabelKey(
    universityId: UniversityId,
    weights: CollegeWeights,
): string {
    return getUniversityModule(universityId).getDisplayedMinRateLabelKey(weights);
}

export function getDepartmentThreshold(universityId: UniversityId, dept: Department): number {
    return getUniversityModule(universityId).getDepartmentThreshold(dept);
}

/** Section-owned info/warning alert shown near the top of the score-entry
 *  step (e.g. FM "rate not announced", AASU medicine apply-floor, IM apply +
 *  last-year). Returns null when the section has no alert for this context. */
export function getScoreInfoAlert(
    universityId: UniversityId,
    college: College,
    department: Department | null,
    track: Track | null | undefined,
    collegeMinHs: number | null,
    enteredHs: number,
): StepAlert | StepAlert[] | null {
    return getUniversityModule(universityId).getScoreInfoAlert(college, department, track, collegeMinHs, enteredHs);
}

/** Section-owned info alert shown on the specialty-selection step. Optional per
 *  section — only modules that export `getSpecialtyInfoAlert` contribute one
 *  (e.g. AASU's "last year's lowest accepted rate not announced"). */
export function getSpecialtyInfoAlert(
    universityId: UniversityId,
    college: College,
    gender?: Gender,
): StepAlert | StepAlert[] | null {
    const mod = getUniversityModule(universityId) as {
        getSpecialtyInfoAlert?: (college: College, gender?: Gender) => StepAlert | StepAlert[] | null;
    };
    return mod.getSpecialtyInfoAlert ? mod.getSpecialtyInfoAlert(college, gender) : null;
}

/** Section-owned lines shown on the results step when the calculated rate is
 *  below the section's target. Each section decides its own wording. */
export function getResultsFailureLines(
    universityId: UniversityId,
    college: College,
    department: Department | null,
    track: Track | null | undefined,
    trackMinHs: number | null,
    minRate: number,
): StepAlertLine[] {
    return getUniversityModule(universityId).getResultsFailureLines(college, department, track, trackMinHs, minRate);
}

export function getRequiredFields(universityId: UniversityId, weights: CollegeWeights): string[] {
    return getUniversityModule(universityId).getRequiredFields(weights);
}

export function calculateEquivalentRate(
    universityId: UniversityId,
    weights: CollegeWeights,
    scores: Scores,
): number {
    return getUniversityModule(universityId).calculateEquivalentRate(weights, scores);
}

export function meetsMinHighSchool(
    universityId: UniversityId,
    weights: CollegeWeights,
    scores: Scores,
): boolean {
    return getUniversityModule(universityId).meetsMinHighSchool(weights, scores);
}

export function meetsMinEnglish(
    universityId: UniversityId,
    weights: CollegeWeights,
    scores: Scores,
): boolean {
    return getUniversityModule(universityId).meetsMinEnglish(weights, scores);
}

export function getCollegeSpecialties(universityId: UniversityId, collegeKey: string, gender?: Gender, track?: Track | null): Department[] {
    return getUniversityModule(universityId).getCollegeSpecialties(collegeKey, gender, track);
}

export function hasSelectableSpecialties(universityId: UniversityId, collegeKey: string, gender?: Gender, track?: Track | null): boolean {
    return getUniversityModule(universityId).hasSelectableSpecialties(collegeKey, gender, track);
}

export function getTrackMinHighSchool(universityId: UniversityId, track: Track | null | undefined): number | null {
    return getUniversityModule(universityId).getTrackMinHighSchool(track);
}

/** Whether the section's per-college minHighSchool acts as a hard apply-floor
 *  (blocks the score-entry step) or as competitive guidance only. The optional
 *  collegeKey lets a section vary the answer per college (KU: soft for
 *  Science & Law, hard elsewhere); sections that ignore it are unaffected. */
export function isCollegeMinHardGate(universityId: UniversityId, collegeKey?: string): boolean {
    const gate = getUniversityModule(universityId).isCollegeMinHardGate as (collegeKey?: string) => boolean;
    return gate(collegeKey);
}

export function getUniversityInfo(id: UniversityId): UniversityInfo | undefined {
    return allUniversities.find((u) => u.id === id);
}

/** For Foreign/Internal Missions, specialties are flat (each is its own
 *  "college"). Used by step components for layout decisions only — not a
 *  per-section rule. */
export function isFlatStructure(universityId: UniversityId): boolean {
    return universityId === "foreign_missions" || universityId === "internal_missions";
}

/** Whether this section requires a gender selection step. */
export function requiresGenderSelection(universityId: UniversityId): boolean {
    return universityId === "paaet";
}
